import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartBar,
  faSpinner,
  faInfoCircle,
  faDownload,
  faExpand,
  faFilter,
  faShip,
  faTruck,
  faPlane,
  faClipboardCheck,
  faExclamationTriangle,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { calculateBusinessHours, KELLANOVA_THRESHOLDS, getPerformanceCategory } from "../utils/businessHours";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Title,
  ChartDataLabels
);

const EnhancedAttentionTimeBarChart = ({
  data,
  isLoading,
  onChartItemClick,
  height = "100%",
}) => {
  // Define better color palette for brackets with higher contrast and opacity
  const bracketColors = {
    green: "rgba(39, 174, 96, 0.85)", // Bright green
    yellow: "rgba(241, 196, 15, 0.85)", // Bright yellow
    red: "rgba(231, 76, 60, 0.85)", // Bright red
  };

  // Define bracket time thresholds - Kellanova scale
  const bracketThresholds = {
    green: KELLANOVA_THRESHOLDS.good, // 0-72 hours is good
    yellow: KELLANOVA_THRESHOLDS.moderate, // 73-121 hours is moderate
    // above 121 hours is red (poor)
  };

  // State for bracket data
  const [bracketData, setBracketData] = useState({});

  // State for additional analytics
  const [analytics, setAnalytics] = useState({
    total: 0,
    greenPercentage: 0,
    yellowPercentage: 0,
    redPercentage: 0,
    averageTime: 0,
    worstEntryPoint: { name: "", value: 0 },
    bestEntryPoint: { name: "", value: 100 },
  });

  // State for expanded chart view
  const [isExpanded, setIsExpanded] = useState(false);

  // State for sorting order
  const [sortOrder, setSortOrder] = useState("desc"); // "asc" or "desc"

  // Helper function to get transport icon based on entry point
  const getTransportIcon = (entryPoint) => {
    if (!entryPoint) return { icon: faChartBar, color: "text-gray-500" };

    const entryPointNum = parseInt(entryPoint, 10);

    // Sea freight entry points
    if ([160, 510, 430].includes(entryPointNum)) {
      return { icon: faShip, color: "text-blue-600" };
    }

    // Road freight entry points
    if ([240].includes(entryPointNum)) {
      return { icon: faTruck, color: "text-green-600" };
    }

    // Air freight entry points
    if ([520, 521, 640].includes(entryPointNum)) {
      return { icon: faPlane, color: "text-purple-600" };
    }

    // Default for unknown entry points
    return { icon: faChartBar, color: "text-gray-500" };
  };

  // Process data when it changes
  useEffect(() => {
    if (!isLoading && data && data.length > 0) {
      calculateBracketData();
    }
  }, [data, isLoading, sortOrder]);

  // Calculate bracket data for the chart
  const calculateBracketData = () => {
    const tempData = {};
    let totalGreen = 0;
    let totalYellow = 0;
    let totalRed = 0;
    let totalValidTimes = 0;
    let totalTimeSum = 0;

    // Process data for each entry point
    if (data && Array.isArray(data)) {
      data.forEach((item) => {
        const entryPoint =
          item.header?.pedimento?.pedimento_entry_point || "Unknown";
        const attentionTime = calculateAttentionTimeValue(
          item.header?.detail?.dates_data
        );

        // Skip invalid times
        if (attentionTime === Number.MAX_VALUE) return;

        totalTimeSum += attentionTime;
        totalValidTimes++;

        if (!tempData[entryPoint]) {
          tempData[entryPoint] = {
            green: 0,
            yellow: 0,
            red: 0,
            total: 0,
            avgTime: 0,
            timeSum: 0,
            validCount: 0,
          };
        }

        // Increment appropriate bracket counter
        tempData[entryPoint].total += 1;
        tempData[entryPoint].timeSum += attentionTime;
        tempData[entryPoint].validCount += 1;

        if (attentionTime <= bracketThresholds.green) {
          tempData[entryPoint].green += 1;
          totalGreen += 1;
        } else if (attentionTime <= bracketThresholds.yellow) {
          tempData[entryPoint].yellow += 1;
          totalYellow += 1;
        } else {
          tempData[entryPoint].red += 1;
          totalRed += 1;
        }
      });
    }

    // Calculate average times for each entry point
    Object.keys(tempData).forEach((entryPoint) => {
      if (tempData[entryPoint].validCount > 0) {
        tempData[entryPoint].avgTime =
          tempData[entryPoint].timeSum / tempData[entryPoint].validCount;
      }
    });

    // Find best and worst entry points based on percentage of green
    let bestEntryPoint = { name: "", value: 0 };
    let worstEntryPoint = { name: "", value: 100 };

    Object.keys(tempData).forEach((entryPoint) => {
      if (tempData[entryPoint].total >= 5) {
        // Only consider entry points with enough data
        const greenPercentage =
          (tempData[entryPoint].green / tempData[entryPoint].total) * 100;

        if (greenPercentage > bestEntryPoint.value) {
          bestEntryPoint = { name: entryPoint, value: greenPercentage };
        }

        if (greenPercentage < worstEntryPoint.value) {
          worstEntryPoint = { name: entryPoint, value: greenPercentage };
        }
      }
    });

    // Sort entry points by total count
    const sortedData = {};
    const sortedEntryPoints = Object.keys(tempData).sort((a, b) => {
      const aCount = tempData[a].total;
      const bCount = tempData[b].total;
      return sortOrder === "desc" ? bCount - aCount : aCount - bCount;
    });

    sortedEntryPoints.forEach((entryPoint) => {
      sortedData[entryPoint] = tempData[entryPoint];
    });

    // Calculate overall analytics
    const total = totalGreen + totalYellow + totalRed;
    const greenPercentage = total > 0 ? (totalGreen / total) * 100 : 0;
    const yellowPercentage = total > 0 ? (totalYellow / total) * 100 : 0;
    const redPercentage = total > 0 ? (totalRed / total) * 100 : 0;
    const averageTime =
      totalValidTimes > 0 ? totalTimeSum / totalValidTimes : 0;

    // Update state with calculated data
    setBracketData(sortedData);
    setAnalytics({
      total,
      greenPercentage,
      yellowPercentage,
      redPercentage,
      averageTime,
      worstEntryPoint,
      bestEntryPoint,
    });
  };

  // Prepare chart data
  const entryPoints = Object.keys(bracketData);

  const chartData = {
    labels: entryPoints,
    datasets: [
      {
        label: `Green (0-${bracketThresholds.green} hours)`,
        data: entryPoints.map(
          (entryPoint) => bracketData[entryPoint]?.green || 0
        ),
        backgroundColor: bracketColors.green,
        borderColor: bracketColors.green.replace("0.85", "1"),
        borderWidth: 1,
        borderRadius: 4,
        hoverBackgroundColor: bracketColors.green.replace("0.85", "0.95"),
      },
      {
        label: `Yellow (${bracketThresholds.green + 1}-${
          bracketThresholds.yellow
        } hours)`,
        data: entryPoints.map(
          (entryPoint) => bracketData[entryPoint]?.yellow || 0
        ),
        backgroundColor: bracketColors.yellow,
        borderColor: bracketColors.yellow.replace("0.85", "1"),
        borderWidth: 1,
        borderRadius: 4,
        hoverBackgroundColor: bracketColors.yellow.replace("0.85", "0.95"),
      },
      {
        label: `Red (${bracketThresholds.yellow + 1}+ hours)`,
        data: entryPoints.map(
          (entryPoint) => bracketData[entryPoint]?.red || 0
        ),
        backgroundColor: bracketColors.red,
        borderColor: bracketColors.red.replace("0.85", "1"),
        borderWidth: 1,
        borderRadius: 4,
        hoverBackgroundColor: bracketColors.red.replace("0.85", "0.95"),
      },
    ],
  };

  // Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    scales: {
      y: {
        stacked: true,
        beginAtZero: true,
        title: {
          display: true,
          text: "Number of References",
          font: {
            size: 12,
            weight: "bold",
          },
        },
        ticks: {
          precision: 0,
        },
        grid: {
          color: "rgba(200, 200, 200, 0.2)",
        },
      },
      x: {
        stacked: true,
        title: {
          display: true,
          text: "Entry Point",
          font: {
            size: 12,
            weight: "bold",
          },
        },
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        position: "top",
        labels: {
          usePointStyle: true,
          padding: 15,
          boxWidth: 10,
          font: {
            size: 11,
          },
        },
      },
      tooltip: {
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        titleColor: "#333",
        bodyColor: "#333",
        bodyFont: {
          size: 12,
        },
        borderColor: "rgba(200, 200, 200, 0.9)",
        borderWidth: 1,
        padding: 10,
        boxPadding: 5,
        usePointStyle: true,
        callbacks: {
          title: (context) => `Entry Point: ${context[0].label}`,
          label: (context) => {
            const datasetLabel = context.dataset.label;
            const value = context.raw;
            return `${datasetLabel}: ${value} references`;
          },
          footer: (context) => {
            const entryPoint = context[0].label;
            const ep = bracketData[entryPoint];
            if (!ep) return "";

            const avgTime = ep.avgTime.toFixed(1);
            let performanceCategory = "";

            if (ep.avgTime <= bracketThresholds.green) {
              performanceCategory = "Good";
            } else if (ep.avgTime <= bracketThresholds.yellow) {
              performanceCategory = "Moderate";
            } else {
              performanceCategory = "Poor";
            }

            return `Average attention time: ${avgTime} hours (${performanceCategory})`;
          },
        },
      },
      title: {
        display: false,
        text: "Attention Time Distribution by Entry Point",
        font: {
          size: 16,
          weight: "bold",
        },
      },
      datalabels: {
        color: "#fff",
        font: {
          weight: "bold",
          size: 10,
        },
        textStrokeColor: "rgba(0, 0, 0, 0.2)",
        textStrokeWidth: 1,
        formatter: (value) => {
          return value > 0 ? value : "";
        },
        display: (context) => {
          // Only show data labels for values > 0
          return context.dataset.data[context.dataIndex] > 0;
        },
      },
    },
    onClick: (_, elements) => {
      if (elements.length > 0 && typeof onChartItemClick === "function") {
        const index = elements[0].index;
        const datasetIndex = elements[0].datasetIndex;
        const entryPoint = chartData.labels[index];

        let bracketType = "";
        if (datasetIndex === 0) bracketType = "green";
        else if (datasetIndex === 1) bracketType = "yellow";
        else if (datasetIndex === 2) bracketType = "red";

        const count = chartData.datasets[datasetIndex].data[index];

        onChartItemClick({
          type: "attentionTime",
          entryPoint: entryPoint,
          bracketType: bracketType,
          count: count,
        });
      }
    },
  };

  // Format a percentage value
  const formatPercentage = (value) => {
    return `${value.toFixed(1)}%`;
  };

  // Render loading spinner when data is loading
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <FontAwesomeIcon
          icon={faSpinner}
          className="animate-spin text-xl text-amber-600 mr-2"
        />
        <span className="text-gray-600">Loading chart data...</span>
      </div>
    );
  }

  // Render "no data" message when there is no data
  if (!data || data.length === 0 || entryPoints.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center h-full">
        <FontAwesomeIcon
          icon={faChartBar}
          className="text-2xl text-gray-400 mb-2"
        />
        <span className="text-gray-500">No attention time data available</span>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-4 w-full h-full flex flex-col">
      {/* Chart Header */}
      <div className="mb-4 flex justify-between items-center">
        <h2 className="text-lg font-bold text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faClock} className="text-amber-600 mr-2" />
          Attention Time by Entry Point
        </h2>

        {/* Sort Controls */}
        <div className="flex space-x-2">
          <button
            className={`px-2 py-1 text-xs rounded-md transition-colors ${
              sortOrder === "desc"
                ? "bg-amber-100 text-amber-700 font-medium"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            onClick={() => setSortOrder("desc")}
            title="Sort by highest volume"
          >
            Highest Volume
          </button>
          <button
            className={`px-2 py-1 text-xs rounded-md transition-colors ${
              sortOrder === "asc"
                ? "bg-amber-100 text-amber-700 font-medium"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            onClick={() => setSortOrder("asc")}
            title="Sort by lowest volume"
          >
            Lowest Volume
          </button>
        </div>
      </div>

      {/* Analytics Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <div className="bg-gray-50 rounded-md p-2">
          <div className="text-xs text-gray-500 mb-1">Avg Attention Time</div>
          <div className="flex items-center">
            <div className="text-md font-bold">
              {analytics.averageTime.toFixed(1)}h
            </div>
          </div>
        </div>

        <div className="bg-green-50 rounded-md p-2">
          <div className="text-xs text-gray-500 mb-1">Good Performance</div>
          <div className="flex items-center">
            <div className="text-md font-bold text-green-700">
              {formatPercentage(analytics.greenPercentage)}
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 rounded-md p-2">
          <div className="text-xs text-gray-500 mb-1">Moderate Performance</div>
          <div className="text-md font-bold text-yellow-700">
            {formatPercentage(analytics.yellowPercentage)}
          </div>
        </div>

        <div className="bg-red-50 rounded-md p-2">
          <div className="text-xs text-gray-500 mb-1">Poor Performance</div>
          <div className="text-md font-bold text-red-700">
            {formatPercentage(analytics.redPercentage)}
          </div>
        </div>
      </div>

      {/* Chart Container */}
      <div className="flex-grow relative">
        <Bar data={chartData} options={options} />
      </div>

      {/* Chart Info Footer */}
      <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between items-center text-xs text-gray-500">
        <div>
          <span>
            Total References: <strong>{analytics.total}</strong>
          </span>
          {analytics.bestEntryPoint.name && (
            <>
              <span className="mx-2">|</span>
              <span>
                Best Entry Point:{" "}
                <strong>{analytics.bestEntryPoint.name}</strong>{" "}
                <span className="text-green-600">
                  ({formatPercentage(analytics.bestEntryPoint.value)} good)
                </span>
              </span>
            </>
          )}
        </div>
        <div className="flex space-x-1">
          {/* These buttons could have actual functionality in a real implementation */}
          <button
            title="Download chart"
            className="p-1 hover:text-amber-600 transition-colors"
          >
            <FontAwesomeIcon icon={faDownload} />
          </button>
          <button
            title="Expand chart"
            className="p-1 hover:text-amber-600 transition-colors"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <FontAwesomeIcon icon={faExpand} />
          </button>
          <button
            title="Filter data"
            className="p-1 hover:text-amber-600 transition-colors"
          >
            <FontAwesomeIcon icon={faFilter} />
          </button>
        </div>
      </div>

      {/* Expanded View Modal */}
      {/* Expanded View Modal with Fixed Alignment */}
      {isExpanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setIsExpanded(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-5/6 h-5/6 max-w-7xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                Attention Time Distribution Analysis
              </h3>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setIsExpanded(false)}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal Content with Fixed Scrolling */}
            <div className="p-4 overflow-y-auto h-full pb-24">
              {/* Analytics Cards Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                {/* Performance Summary Card */}
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    Performance Summary
                  </h4>
                  <div className="flex items-center mb-2">
                    <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                    <div className="text-sm">
                      Good: {formatPercentage(analytics.greenPercentage)}
                    </div>
                  </div>
                  <div className="flex items-center mb-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                    <div className="text-sm">
                      Moderate: {formatPercentage(analytics.yellowPercentage)}
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                    <div className="text-sm">
                      Poor: {formatPercentage(analytics.redPercentage)}
                    </div>
                  </div>
                  <div className="mt-4 text-xs text-gray-500">
                    Based on {analytics.total} references with valid attention
                    time data
                  </div>
                </div>

                {/* Best & Worst Entry Points Card */}
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    Performance by Entry Point
                  </h4>
                  {analytics.bestEntryPoint.name && (
                    <div className="mb-3">
                      <div className="text-xs text-gray-500">
                        Best Performance
                      </div>
                      <div className="flex items-center">
                        <FontAwesomeIcon
                          icon={
                            getTransportIcon(analytics.bestEntryPoint.name).icon
                          }
                          className={`${
                            getTransportIcon(analytics.bestEntryPoint.name)
                              .color
                          } mr-2`}
                        />
                        <div className="text-sm font-medium">
                          Entry Point {analytics.bestEntryPoint.name}:{" "}
                          {formatPercentage(analytics.bestEntryPoint.value)}{" "}
                          good
                        </div>
                      </div>
                    </div>
                  )}
                  {analytics.worstEntryPoint.name && (
                    <div>
                      <div className="text-xs text-gray-500">
                        Needs Improvement
                      </div>
                      <div className="flex items-center">
                        <FontAwesomeIcon
                          icon={
                            getTransportIcon(analytics.worstEntryPoint.name)
                              .icon
                          }
                          className={`${
                            getTransportIcon(analytics.worstEntryPoint.name)
                              .color
                          } mr-2`}
                        />
                        <div className="text-sm font-medium">
                          Entry Point {analytics.worstEntryPoint.name}:{" "}
                          {formatPercentage(analytics.worstEntryPoint.value)}{" "}
                          good
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Time Brackets Explanation Card */}
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    Time Brackets
                  </h4>
                  <div className="flex items-center mb-2">
                    <div className="bg-green-500 text-white w-6 h-6 rounded-full flex items-center justify-center mr-2">
                      <FontAwesomeIcon
                        icon={faClipboardCheck}
                        className="text-xs"
                      />
                    </div>
                    <div className="text-sm">
                      <strong>Good:</strong> 0-{bracketThresholds.green} hours
                    </div>
                  </div>
                  <div className="flex items-center mb-2">
                    <div className="bg-yellow-500 text-white w-6 h-6 rounded-full flex items-center justify-center mr-2">
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="text-xs"
                      />
                    </div>
                    <div className="text-sm">
                      <strong>Moderate:</strong> {bracketThresholds.green + 1}-
                      {bracketThresholds.yellow} hours
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="bg-red-500 text-white w-6 h-6 rounded-full flex items-center justify-center mr-2">
                      <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className="text-xs"
                      />
                    </div>
                    <div className="text-sm">
                      <strong>Poor:</strong> {bracketThresholds.yellow + 1}+
                      hours
                    </div>
                  </div>
                </div>
              </div>

              {/* Chart with Fixed Height */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-5">
                <div style={{ height: "350px" }}>
                  <Bar data={chartData} options={options} />
                </div>
              </div>

              {/* Table View */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Entry Point Details
                </h4>
                <div className="overflow-x-auto">
                  <div className="inline-block min-w-full align-middle">
                    <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th
                              scope="col"
                              className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Entry Point
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Total
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Good (0-{bracketThresholds.green}h)
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Moderate ({bracketThresholds.green + 1}-
                              {bracketThresholds.yellow}h)
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Poor ({bracketThresholds.yellow + 1}h+)
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Avg Time
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {entryPoints.map((entryPoint) => {
                            const ep = bracketData[entryPoint];
                            const greenPct =
                              ep.total > 0 ? (ep.green / ep.total) * 100 : 0;
                            const yellowPct =
                              ep.total > 0 ? (ep.yellow / ep.total) * 100 : 0;
                            const redPct =
                              ep.total > 0 ? (ep.red / ep.total) * 100 : 0;

                            let performanceClass = "";
                            if (ep.avgTime <= bracketThresholds.green) {
                              performanceClass = "text-green-700 bg-green-50";
                            } else if (ep.avgTime <= bracketThresholds.yellow) {
                              performanceClass = "text-yellow-700 bg-yellow-50";
                            } else {
                              performanceClass = "text-red-700 bg-red-50";
                            }

                            return (
                              <tr key={entryPoint}>
                                <td className="px-3 py-2 whitespace-nowrap text-sm">
                                  <div className="flex items-center">
                                    <FontAwesomeIcon
                                      icon={getTransportIcon(entryPoint).icon}
                                      className={`${
                                        getTransportIcon(entryPoint).color
                                      } mr-2`}
                                    />
                                    {entryPoint}
                                  </div>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm font-medium">
                                  {ep.total}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm">
                                  {ep.green} ({formatPercentage(greenPct)})
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm">
                                  {ep.yellow} ({formatPercentage(yellowPct)})
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm">
                                  {ep.red} ({formatPercentage(redPct)})
                                </td>
                                <td
                                  className={`px-3 py-2 whitespace-nowrap text-sm font-medium ${performanceClass} rounded-md`}
                                >
                                  {ep.avgTime.toFixed(1)}h
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to calculate attention time in business hours (excluding weekends)
const calculateAttentionTimeValue = (datesData) => {
  const dateInit = new Date(datesData?.date_init);
  const dateCrossing = new Date(datesData?.date_crossing);
  if (isNaN(dateInit.getTime()) || isNaN(dateCrossing.getTime())) {
    return Number.MAX_VALUE;
  }
  
  // Calculate business hours excluding weekends
  const businessHours = calculateBusinessHours(dateInit, dateCrossing);
  return businessHours !== null ? businessHours : Number.MAX_VALUE;
};

export default EnhancedAttentionTimeBarChart;
